'use client';
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useColorScheme } from '#pplx/frontend/aether/hooks/useColorScheme.js';
import { useTimeSpentTracker } from '#pplx/frontend/apps/ask/analytics/useTimeSpentTracker.js';
import { bumpSessionIdExpiration } from '#pplx/frontend/apps/ask/browser-state/cookieManager.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import React, { useCallback, useEffect, useState } from 'react';
// Single-file event: defined here at its only call site rather than in a shared package.
const pageviewEvent = EVENT_PAYLOAD;
const PageviewTemplate = React.memo(function PageviewTemplate({ children }) {
    const pathname = usePathname();
    const params = useSearchParams();
    const [lastTrackedPath, setLastTrackedPath] = useState(null);
    const { colorScheme } = useColorScheme();
    const { track, enabled } = useInstrumentation();
    useTimeSpentTracker();
    const ignorePath = useCallback((path) => {
        return ((path === '/search' && params?.get('newFrontendContextUUID')) ||
            (path.startsWith('/page/new') && params?.get('newFrontendContextUUID')) ||
            path === '/search/new');
    }, [params]);
    /*
     * This function is used to track pageviews:
     * - It is called when the component is mounted (user refreshs or lands on permalink)
     * - It is also called and when the user navigates within the site internally
     */
    const pageview = useCallback((path) => {
        const samePath = (path?.split('?')[0] ?? '') === (lastTrackedPath?.split('?')[0] ?? '');
        if (!enabled ||
            samePath ||
            ignorePath(path) ||
            // we only want to track pageviews when the page is actually visible
            // otherwise we're tracking preloads as views
            document.visibilityState !== 'visible') {
            return;
        }
        // internalReferrer is the last pageview tracked within this SPA shell
        const internalReferrer = lastTrackedPath;
        setLastTrackedPath(path);
        // Bump session ID expiration on new pageview
        bumpSessionIdExpiration();
        track(pageviewEvent({
            name: 'pageview',
            data: {
                internalReferrer: internalReferrer ?? undefined,
                colorScheme,
            },
        }));
    }, [colorScheme, enabled, ignorePath, lastTrackedPath, track]);
    useEffect(() => {
        const firePageview = () => {
            if (pathname)
                pageview(pathname);
        };
        firePageview();
        document.addEventListener('visibilitychange', firePageview);
        return () => {
            document.removeEventListener('visibilitychange', firePageview);
        };
    }, [pageview, pathname]);
    // Bump session ID expiration on visibility / focus change
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                bumpSessionIdExpiration();
            }
        };
        const handleFocus = () => {
            bumpSessionIdExpiration();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);
    return _jsx(_Fragment, { children: children });
});
export default PageviewTemplate;
//# sourceMappingURL=PageviewTemplate.js.map